/* LabMilo · lm-antes-despues · comparador antes/después (JS vanilla, sin librerías)
 *
 * El estado vive en el <input type="range"> nativo:
 * - ratón y teclado: los gestiona el propio range (clic en cualquier punto, arrastre, flechas,
 *   Re Pág / Av Pág, Inicio / Fin);
 * - dedo o lápiz: además se sigue el gesto con Pointer Events, porque Safari de iOS solo mueve
 *   un range si se toca justo el pulgar. Un gesto vertical sigue desplazando la página y el
 *   pellizco amplía (touch-action: pan-y pinch-zoom); el comparador solo se mueve con gesto
 *   horizontal o con un toque. Mientras dura el gesto no se acepta el valor que pone el range
 *   nativo (Chrome de Android lo cambia ya al tocar); si el navegador se queda el gesto, se
 *   restaura el valor del inicio.
 * Este script solo pinta la posición (--lm-ad-pos) y el aria-valuetext.
 */
(function () {
  if (!window.customElements || customElements.get('lm-antes-despues')) return;

  var MOVE_THRESHOLD = 6; // px antes de decidir si el gesto es horizontal

  function clamp(value) {
    return Math.min(100, Math.max(0, value));
  }

  class LmAntesDespues extends HTMLElement {
    constructor() {
      super();
      this.onInput = this.onInput.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onPointerCancel = this.onPointerCancel.bind(this);
      this.gesture = null;
    }

    connectedCallback() {
      this.box = this.querySelector('.lm-ad__box');
      this.range = this.querySelector('.lm-ad__range');
      if (!this.box || !this.range) return;

      this.labelBefore = this.dataset.labelBefore || '';
      this.labelAfter = this.dataset.labelAfter || '';

      this.range.addEventListener('input', this.onInput);
      this.box.addEventListener('pointerdown', this.onPointerDown);
      this.box.addEventListener('pointermove', this.onPointerMove);
      this.box.addEventListener('pointerup', this.onPointerUp);
      this.box.addEventListener('pointercancel', this.onPointerCancel);
      this.box.addEventListener('lostpointercapture', this.onPointerCancel);

      this.render(this.range.value);
    }

    disconnectedCallback() {
      if (!this.box || !this.range) return;
      this.range.removeEventListener('input', this.onInput);
      this.box.removeEventListener('pointerdown', this.onPointerDown);
      this.box.removeEventListener('pointermove', this.onPointerMove);
      this.box.removeEventListener('pointerup', this.onPointerUp);
      this.box.removeEventListener('pointercancel', this.onPointerCancel);
      this.box.removeEventListener('lostpointercapture', this.onPointerCancel);
      this.gesture = null;
    }

    onInput() {
      // Mientras dura un gesto con el dedo o el lápiz, el range nativo no manda: Chrome de Android
      // lo mueve ya en el touchstart, antes de saber si el gesto es un desplazamiento vertical.
      // Antes del arrastre se mantiene el valor del inicio; durante el arrastre, el del gesto.
      var g = this.gesture;
      if (g) {
        var keep = g.dragging && g.value != null ? g.value : g.startValue;
        this.range.value = String(keep);
        this.render(keep);
        return;
      }
      this.render(this.range.value);
    }

    render(value) {
      var v = clamp(Number(value));
      if (isNaN(v)) v = 50;
      this.box.style.setProperty('--lm-ad-pos', v + '%');
      this.box.style.setProperty('--lm-ad-p', String(v));
      if (this.labelBefore && this.labelAfter) {
        var shown = Math.round(v);
        this.range.setAttribute(
          'aria-valuetext',
          this.labelBefore + ': ' + shown + ' %, ' + this.labelAfter + ': ' + (100 - shown) + ' %'
        );
      }
    }

    setFromClientX(clientX) {
      var rect = this.box.getBoundingClientRect();
      if (!rect.width) return;
      var v = Math.round(clamp(((clientX - rect.left) / rect.width) * 100));
      if (this.gesture) this.gesture.value = v;
      this.range.value = String(v);
      this.render(v);
    }

    onPointerDown(event) {
      // El ratón lo gestiona el range nativo (clic y arrastre).
      if (event.pointerType === 'mouse' || !event.isPrimary) return;
      this.gesture = {
        id: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        startValue: Number(this.range.value),
        dragging: false,
        value: null,
      };
    }

    onPointerMove(event) {
      var g = this.gesture;
      if (!g || event.pointerId !== g.id) return;
      if (!g.dragging) {
        var dx = Math.abs(event.clientX - g.startX);
        var dy = Math.abs(event.clientY - g.startY);
        if (dx < MOVE_THRESHOLD || dx <= dy) return;
        g.dragging = true;
      }
      this.setFromClientX(event.clientX);
    }

    onPointerUp(event) {
      var g = this.gesture;
      if (!g || event.pointerId !== g.id) return;
      this.gesture = null;
      if (g.dragging) {
        this.setFromClientX(event.clientX);
        return;
      }
      // Un toque (sin desplazarse) lleva el tirador a ese punto; un deslizamiento vertical no.
      var moved = Math.max(Math.abs(event.clientX - g.startX), Math.abs(event.clientY - g.startY));
      if (moved < MOVE_THRESHOLD) this.setFromClientX(event.clientX);
    }

    onPointerCancel(event) {
      var g = this.gesture;
      if (!g || event.pointerId !== g.id) return;
      // El navegador ha tomado el gesto (desplazamiento vertical o pellizco): si aún no había
      // arrastre, el tirador vuelve al valor del inicio por si el range nativo lo había movido.
      if (!g.dragging) {
        this.range.value = String(g.startValue);
        this.render(g.startValue);
      }
      this.gesture = null;
    }
  }

  customElements.define('lm-antes-despues', LmAntesDespues);
})();
